import * as React from 'react';

/**
 * Wiring a label to its control, and an error message to the input it
 * describes, is fiddly and easy to skip. Field puts the generated ids on this
 * context and the control components read them, so the accessibility plumbing
 * happens whether or not anyone remembers it.
 */
export interface FieldContextValue {
  /** id for the control itself; the label's htmlFor points at it. */
  controlId: string;
  /** id of the helper text, if any. */
  descriptionId?: string;
  /** id of the validation message, if any. */
  messageId?: string;
  /** True when the field is showing a validation error. */
  invalid: boolean;
  disabled: boolean;
  required: boolean;
}

export const FieldContext = React.createContext<FieldContextValue | null>(null);

/**
 * Read the surrounding Field, if there is one. Returns null for controls used
 * outside a Field, which is allowed — they just manage their own ids.
 */
export function useFieldContext(): FieldContextValue | null {
  return React.useContext(FieldContext);
}

/**
 * Props every control in the library derives from its Field. Merged with any
 * explicitly-passed values, which always win.
 */
export function useFieldControlProps(overrides: {
  id?: string;
  disabled?: boolean;
  required?: boolean;
  'aria-describedby'?: string;
  // React's own type, which also allows "grammar" and "spelling".
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
}) {
  const field = useFieldContext();
  if (!field) return overrides;

  const describedBy =
    [field.descriptionId, field.messageId, overrides['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined;

  return {
    ...overrides,
    id: overrides.id ?? field.controlId,
    disabled: overrides.disabled ?? field.disabled,
    required: overrides.required ?? field.required,
    'aria-describedby': describedBy,
    'aria-invalid': overrides['aria-invalid'] ?? (field.invalid || undefined),
  };
}
